import './styles/archives.scss';

import { ImageBlock, InputButton } from '../components/Primitives';
import React, { useEffect, useMemo, useState } from 'react';

import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import { PartsListing } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/ProductListing';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { TitleBanner } from '../components/Primitives';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';
import { IGatsbyImageData } from 'gatsby-plugin-image';

export const query = graphql`
    query Archives {
        parts: allProductsJson(
            filter: { category: { regex: "^/Drolet/Heaters/" } }
            sort: { title: { en_CA: ASC } }
        ) {
            nodes {
                code
                id
                category
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                documents {
                    en_CA
                    en_US
                    fr_CA
                    caption {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                parts {
                    code
                    id
                    title {
                        en_CA
                        en_US
                        fr_CA
                    }
                    attributes {
                        schema_number {
                            group
                            name {
                                en_CA
                                en_US
                                fr_CA
                            }
                            values {
                                en_CA
                                en_US
                                fr_CA
                            }
                        }
                        nb_per_appliance {
                            group
                            name {
                                en_CA
                                en_US
                                fr_CA
                            }
                            values {
                                en_CA
                                en_US
                                fr_CA
                            }
                        }
                    }
                }
                attributes {
                    available_country {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    serial_number_from {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                        group
                    }
                    serial_number_to {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        cms: allProductsJson(filter: { code: { eq: "drolet_archives" } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Archives({ data } : { data: Queries.ArchivesQuery}) {
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useQueryArg('s');
    const products = data.parts.nodes;
    const isMobile = useMobileDetect().isMobile();
    const cms = data?.cms?.nodes?.[0];
    const responsiveImages = cms?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp?.gatsbyImageData;

    const { currentLocale } = usePageLocalization();
    const bannerTitle = cms?.title?.[currentLocale];

    const filteredProducts = useMemo(
        () =>
            products.filter((product: any) => {
                const prodTitle = product.title?.[currentLocale]
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                const prodCode = product.code
                    ?.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                const normalizedSearch = search
                    ?.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');            
                return (
                    prodTitle?.includes(normalizedSearch) ||
                    prodCode?.includes(normalizedSearch) /*  &&
                    (displayInCurrentCountry ||
                        !product?.attributes?.available_country) */
                );
            }),
        [currentLocale, products, search]
    );

    useEffect(() => {
        setSearch(searchTerm);
    }, [searchTerm]);

    return (
        <Layout>
            <SEO
                title={cms?.attributes?.meta_title?.values?.[0]?.[currentLocale] as string}
                description={
                    cms?.attributes?.meta_description?.values?.[0]?.[currentLocale] as string
                }
            />
            <TitleBanner
                className='archives-banner'
                imageData={isMobile ? mobileImage : desktopImage}
                title={bannerTitle as string}
            />
            <div className='container'>
                <div className='archives-page'>
                    <aside>
                        <ArchiveSearch
                            search={search}
                            setSearch={setSearch}
                            setSearchTerm={setSearchTerm}
                        />
                    </aside>

                    <PartsListing products={filteredProducts} />
                </div>

                <ImageBlock
                    subtitle={
                        cms?.attributes?.texte_1?.values?.[0]?.[currentLocale] as string
                    }
                    title={
                        cms?.attributes?.texte_2?.values?.[0]?.[currentLocale] as string
                    }
                    content={
                        cms?.attributes?.content?.values?.[0]?.[currentLocale] as string
                    } 
                    image={
                        cms?.fields?.images?.[0]?.childImageSharp
                            ?.gatsbyImageData as IGatsbyImageData 
                    }
                />
            </div>
        </Layout>
    );
}

function ArchiveSearch(props: {
    search: string;
    setSearch: any;
    setSearchTerm: any;
}) {
    const { setSearch, setSearchTerm, search } = props;
    const [localSearch, setLocalSearch] = useState(search);
    const { t } = useTranslation('pages');

    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    return (
        <div className='archives-search'>
            <h4>{t('archives.search-title')}</h4>
            <InputButton
                label={t('archives.search-btn')}
                value={localSearch}
                setValue={(value) => setLocalSearch(value)}
                onBtnClick={() => {
                    setSearch(localSearch);
                    setSearchTerm(localSearch);
                }}
                placeholder={t('archives.search-placeholder') as string}
            />
        </div>
    );
}
